export type RomPlatform = 'gb' | 'gbc' | 'gba' | 'nds';

export interface RomFileDescriptor {
  name: string;
  sizeBytes: number;
  extension: string | null;
}

export interface RomInspectionResult extends RomFileDescriptor {
  platform: RomPlatform;
  sha256: string;
  internalTitle: string | null;
  gameCode: string | null;
  revision: string | null;
}

const SHA256_K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function rotateRight(value: number, shift: number): number {
  return (value >>> shift) | (value << (32 - shift));
}

class Sha256 {
  private readonly state = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  private readonly block = new Uint8Array(64);
  private blockLength = 0;
  private bytesHashed = 0;
  private finished = false;

  update(input: Uint8Array): this {
    if (this.finished) throw new Error('SHA256_ALREADY_FINALIZED');
    this.bytesHashed += input.length;

    let offset = 0;
    while (offset < input.length) {
      const take = Math.min(64 - this.blockLength, input.length - offset);
      this.block.set(input.subarray(offset, offset + take), this.blockLength);
      this.blockLength += take;
      offset += take;

      if (this.blockLength === 64) {
        this.processBlock(this.block);
        this.blockLength = 0;
      }
    }

    return this;
  }

  digestHex(): string {
    if (this.finished) throw new Error('SHA256_ALREADY_FINALIZED');
    this.finished = true;

    const finalBlock = new Uint8Array(128);
    finalBlock.set(this.block.subarray(0, this.blockLength));
    finalBlock[this.blockLength] = 0x80;

    const padLength = this.blockLength < 56 ? 64 : 128;
    const bitLengthHigh = Math.floor(this.bytesHashed / 0x20000000) >>> 0;
    const bitLengthLow = (this.bytesHashed << 3) >>> 0;
    const view = new DataView(finalBlock.buffer);
    view.setUint32(padLength - 8, bitLengthHigh, false);
    view.setUint32(padLength - 4, bitLengthLow, false);

    this.processBlock(finalBlock.subarray(0, 64));
    if (padLength === 128) this.processBlock(finalBlock.subarray(64, 128));

    return Array.from(this.state)
      .map((word) => word.toString(16).padStart(8, '0'))
      .join('');
  }

  private processBlock(block: Uint8Array): void {
    const words = new Uint32Array(64);
    const view = new DataView(block.buffer, block.byteOffset, block.byteLength);

    for (let index = 0; index < 16; index += 1) {
      words[index] = view.getUint32(index * 4, false);
    }

    for (let index = 16; index < 64; index += 1) {
      const w15 = words[index - 15]!;
      const w2 = words[index - 2]!;
      const s0 = rotateRight(w15, 7) ^ rotateRight(w15, 18) ^ (w15 >>> 3);
      const s1 = rotateRight(w2, 17) ^ rotateRight(w2, 19) ^ (w2 >>> 10);
      words[index] = (words[index - 16]! + s0 + words[index - 7]! + s1) >>> 0;
    }

    let a = this.state[0]!;
    let b = this.state[1]!;
    let c = this.state[2]!;
    let d = this.state[3]!;
    let e = this.state[4]!;
    let f = this.state[5]!;
    let g = this.state[6]!;
    let h = this.state[7]!;

    for (let index = 0; index < 64; index += 1) {
      const sigma1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temp1 = (h + sigma1 + choice + SHA256_K[index]! + words[index]!) >>> 0;
      const sigma0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (sigma0 + majority) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    this.state[0] = (this.state[0]! + a) >>> 0;
    this.state[1] = (this.state[1]! + b) >>> 0;
    this.state[2] = (this.state[2]! + c) >>> 0;
    this.state[3] = (this.state[3]! + d) >>> 0;
    this.state[4] = (this.state[4]! + e) >>> 0;
    this.state[5] = (this.state[5]! + f) >>> 0;
    this.state[6] = (this.state[6]! + g) >>> 0;
    this.state[7] = (this.state[7]! + h) >>> 0;
  }
}

export function getFileExtension(fileName: string): string | null {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex <= 0 || dotIndex === fileName.length - 1) return null;
  return fileName.slice(dotIndex + 1).toLowerCase();
}

export function describeRomFile(name: string, sizeBytes: number): RomFileDescriptor {
  return {
    name,
    sizeBytes,
    extension: getFileExtension(name),
  };
}

export function inferPlatform(fileName: string, header?: Uint8Array): RomPlatform {
  const extension = getFileExtension(fileName);

  if (extension === 'gba') return 'gba';
  if (extension === 'nds') return 'nds';
  if (extension === 'gbc') return 'gbc';
  if (extension === 'gb') {
    const cgbFlag = header && header.length > 0x143 ? header[0x143] : undefined;
    return cgbFlag === 0x80 || cgbFlag === 0xc0 ? 'gbc' : 'gb';
  }

  throw new Error(`UNSUPPORTED_ROM_EXTENSION:${extension ?? 'none'}`);
}

function readAscii(bytes: Uint8Array, start: number, length: number): string | null {
  if (start < 0 || length <= 0 || start + length > bytes.length) return null;
  let value = '';
  for (let index = start; index < start + length; index += 1) {
    const code = bytes[index]!;
    if (code === 0) break;
    value += code >= 0x20 && code <= 0x7e ? String.fromCharCode(code) : ' ';
  }
  const normalized = value.trim().replace(/\s+/g, ' ');
  return normalized.length > 0 ? normalized : null;
}

export function inspectRomHeader(
  fileName: string,
  header: Uint8Array,
): Pick<RomInspectionResult, 'platform' | 'internalTitle' | 'gameCode' | 'revision'> {
  const platform = inferPlatform(fileName, header);

  if (platform === 'gba') {
    if (header.length < 0xbd) throw new Error('ROM_HEADER_TOO_SMALL:GBA');
    if (header[0xb2] !== 0x96) throw new Error('INVALID_GBA_HEADER');
    return {
      platform,
      internalTitle: readAscii(header, 0xa0, 12),
      gameCode: readAscii(header, 0xac, 4),
      revision: String(header[0xbc] ?? 0),
    };
  }

  if (platform === 'nds') {
    if (header.length < 0x20) throw new Error('ROM_HEADER_TOO_SMALL:NDS');
    return {
      platform,
      internalTitle: readAscii(header, 0x00, 12),
      gameCode: readAscii(header, 0x0c, 4),
      revision: String(header[0x1e] ?? 0),
    };
  }

  if (header.length < 0x150) throw new Error('ROM_HEADER_TOO_SMALL:GB');
  return {
    platform,
    internalTitle: readAscii(header, 0x134, platform === 'gbc' ? 15 : 16),
    gameCode: null,
    revision: String(header[0x14c] ?? 0),
  };
}

export function hashBytesSha256(bytes: Uint8Array): string {
  return new Sha256().update(bytes).digestHex();
}

export async function hashBlobSha256(
  blob: Blob,
  chunkSizeBytes = 4 * 1024 * 1024,
): Promise<string> {
  if (!Number.isSafeInteger(chunkSizeBytes) || chunkSizeBytes < 64 * 1024) {
    throw new Error('INVALID_HASH_CHUNK_SIZE');
  }

  const hasher = new Sha256();
  for (let offset = 0; offset < blob.size; offset += chunkSizeBytes) {
    const chunk = blob.slice(offset, Math.min(offset + chunkSizeBytes, blob.size));
    hasher.update(new Uint8Array(await chunk.arrayBuffer()));
  }
  return hasher.digestHex();
}

export async function inspectRomFile(file: File): Promise<RomInspectionResult> {
  const extension = getFileExtension(file.name);
  if (!extension) throw new Error('ROM_EXTENSION_REQUIRED');

  const headerLength = Math.min(file.size, 0x200);
  const header = new Uint8Array(await file.slice(0, headerLength).arrayBuffer());
  const parsed = inspectRomHeader(file.name, header);
  const sha256 = await hashBlobSha256(file);

  return {
    name: file.name,
    sizeBytes: file.size,
    extension,
    sha256,
    ...parsed,
  };
}
